import React, { useState } from 'react'
import { IoPhonePortraitOutline } from 'react-icons/io5'
import { useForm } from 'react-hook-form'
import useAuth from '../../context/userContext'
import {
  buildDefaultSenderId,
  createOrUpdateSenderId,
  isReservedSenderId,
  getReservedSenderIdMessage,
  sanitizeSmsSenderIdInput,
  SMS_SENDER_ID_MAX_LENGTH,
  SMS_SENDER_ID_PATTERN,
} from '../../utils/smsSenderId'

const choices = [
  { id: 1, label: 'Yes', value: 'yes' },
  { id: 2, label: 'No', value: 'no' },
]

const RequestSenderIdForm = ({
  onSuccess,
  compact = false,
  existingSenderId = null,
  currentSenderId = null,
}) => {
  const isUpdate = Boolean(existingSenderId)
  const [selectedChoice, setSelectedChoice] = useState(null)
  const { user, setUser } = useAuth()
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm()

  const onSubmit = async (data) => {
    const senderId =
      selectedChoice === 'yes'
        ? (data.username || '').trim()
        : buildDefaultSenderId(user) || `${user?.sms_sender_id || ''}`

    if (!senderId) {
      return
    }

    const result = await createOrUpdateSenderId(senderId, {
      user,
      setUser,
      existingId: existingSenderId,
    })

    if (result.ok) {
      reset()
      setSelectedChoice(null)
      onSuccess?.(result.data)
    }
  }

  const placeholder =
    user?.username || buildDefaultSenderId(user) || ''

  const usernameField = register('username', {
    required: selectedChoice === 'yes' ? 'Custom SMS Nickname is required' : false,
    maxLength: { value: SMS_SENDER_ID_MAX_LENGTH, message: 'Maximum 11 characters' },
    minLength: { value: 1, message: 'SMS Nickname is required' },
    pattern: {
      value: SMS_SENDER_ID_PATTERN,
      message: 'Only letters, numbers, and spaces are allowed',
    },
    validate: (value) =>
      !isReservedSenderId(value) || getReservedSenderIdMessage(),
  })

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`border dark:border-border-dark border-gray-200 rounded-lg bg-white dark:bg-secondaryDark ${
        compact ? 'p-4' : 'p-6'
      }`}
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 bg-yellowcolor/20 rounded-full flex items-center justify-center shrink-0">
          <IoPhonePortraitOutline className="text-yellowcolor text-xl" />
        </div>
        <div>
          <h2 className="text-base font-bold dark:text-white">
            {isUpdate ? 'Update SMS Nickname' : 'Request a New SMS Nickname'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isUpdate
              ? 'You already have a SMS Nickname on file. Submit a new value to update your existing request.'
              : 'Submit a custom SMS Nickname for approval, or use the default ID for your campaigns.'}
          </p>
          {isUpdate && currentSenderId && (
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
              Current SMS Nickname:{' '}
              <span className="font-semibold">{currentSenderId}</span>
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium dark:text-white mb-2">
            Do you want to use a custom ID for sending SMS?
          </p>
          <div className="flex items-center gap-6">
            {choices.map((choice) => (
              <label key={choice.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  {...register('choice', { required: 'Please select an option' })}
                  value={choice.value}
                  checked={selectedChoice === choice.value}
                  onChange={(e) => {
                    setSelectedChoice(e.target.value)
                    if (choice.value === 'no') {
                      setValue('username', buildDefaultSenderId(user))
                    }
                  }}
                  className="form-radio text-yellowcolor focus:ring-yellowcolor"
                />
                <span className="dark:text-white text-sm">{choice.label}</span>
              </label>
            ))}
          </div>
          {errors.choice && (
            <p className="text-red-500 text-xs mt-1">{errors.choice.message}</p>
          )}
        </div>

        {selectedChoice === 'yes' ? (
          <div>
            <label htmlFor="sender-id-custom" className="block text-sm font-medium mb-1 dark:text-white">
              Custom ID
            </label>
            <input
              type="text"
              id="sender-id-custom"
              placeholder={`eg. ${placeholder}`}
              {...usernameField}
              onChange={(e) => {
                e.target.value = sanitizeSmsSenderIdInput(e.target.value)
                usernameField.onChange(e)
              }}
              maxLength={SMS_SENDER_ID_MAX_LENGTH}
              className="w-full bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
            />
            <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
              Maximum of 11 characters (letters, numbers, and spaces). SOLIDARIDAD and HARVSTALERT are reserved.
            </p>
            {errors.username && (
              <p className="text-red-500 text-xs mt-1">{errors.username.message}</p>
            )}
          </div>
        ) : selectedChoice === 'no' ? (
          <></>
        ) : null}

        {selectedChoice !== 'no' && <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || !selectedChoice}
            className="px-6 py-2 bg-yellowcolor hover:bg-yellowcolor/90 text-black rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? 'Submitting...'
              : isUpdate
                ? 'Update SMS Nickname'
                : 'Submit Request'}
          </button>
        </div>}
      </div>
    </form>
  )
}

export default RequestSenderIdForm
